"use client";

import React, { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, Music, Sparkles } from "lucide-react";
import { Modal } from "@/components/atoms/Modal";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { generateAudio } from "@/lib/actions";
import { AppContext } from "@/lib/contexts";
import { AppReducerActionType, RequestType, UserPremiumType } from "@/types";
interface AddAudioModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
  onSubmit: () => void;
  requestType: RequestType;
}
type FormValues = {
  [key: string]: string;
};
export function AddAudioModal({
  isOpen,
  onClose,
  videoUrl,
  onSubmit,
  requestType
}: AddAudioModalProps) {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [prompt, setPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [maxDuration, setMaxDuration] = useState(0);
  const [formValues, setFormValues] = useState<FormValues>({
    Duration: "5"
  });
  const handleAudioSubmit = useCallback(async (prompt: string, videoUrl: string, requestType: RequestType, formValues: FormValues) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      toast.error("You need to be a subscribed user to use this feature.");
      return;
    }
    if (state.gold < parseInt(formValues["Duration"])) {
      dispatch({
        type: AppReducerActionType.SHOW_PRICING_TABLE,
        payload: {
          ...state,
          showPricingTable: true
        }
      });
      return;
    }
    const formData = new FormData();
    formData.append("prompt", prompt);
    formData.append("referenceVideoUrl", videoUrl);
    formData.append("model", JSON.stringify({
      model: "MMAudio V2",
      steps: "25",
      cfg: "4.5",
      sampler: "None"
    }));
    formData.append("requestType", requestType);
    for (const [key, value] of Object.entries(formValues)) {
      formData.append(`requestFields[${key}]`, value);
    }
    setLoading(true);
    const {
      requestId,
      error
    } = await generateAudio(formData);
    setLoading(false);
    if (error) {
      toast.error("An error occurred while submitting the request.");
      return;
    }
    if (requestId) {
      dispatch({
        type: AppReducerActionType.SET_GOLD,
        payload: {
          ...state,
          gold: state.gold - parseInt(formValues["Duration"])
        }
      });
      onSubmit();
    }
  }, [dispatch, onSubmit, state]);
  useEffect(() => {
    if (videoUrl) {
      const video = document.createElement("video");
      video.src = videoUrl;
      video.onloadedmetadata = () => {
        const videoDuration = Math.ceil(video.duration);
        setMaxDuration(videoDuration);
        setFormValues(prev => {
          return {
            ...prev,
            Duration: Math.min(5, videoDuration).toString()
          };
        });
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl]);
  const handleCancel = () => {
    setLoading(false);
    setPrompt("");
    setNegativePrompt("");
    setFormValues(prev => {
      return {
        ...prev,
        Duration: "5"
      };
    });
    onClose();
  };
  const handleSubmit = () => {
    handleAudioSubmit(prompt, videoUrl, requestType, formValues);
  };
  return <Modal open={isOpen} handleBackdropClick={onClose} disableBackdropClick data-sentry-element="Modal" data-sentry-component="AddAudioModal" data-sentry-source-file="index.tsx">
            <div className="relative mx-auto my-4 rounded-lg p-6 shadow-xl overflow-y-auto max-h-[calc(90vh-2rem)] max-w-xl bg-slate-950">
                <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-center text-white">
                    Add Audio to Video
                </h2>
                <div className="space-y-6">
                    <div className="space-y-3">
                        <Label htmlFor="prompt" className="text-gray-200" data-sentry-element="Label" data-sentry-source-file="index.tsx">
                            Prompt:
                        </Label>
                        <Textarea id="prompt" value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Describe the audio you want to add..." className="resize-none focus:!ring-gray-600 focus:!border-gray-600 transition-all duration-300 placeholder-gray-500 h-32 bg-slate-900 text-gray-200 border-gray-700" data-sentry-element="Textarea" data-sentry-source-file="index.tsx" />
                    </div>
                    <div className="space-y-3">
                        <Label htmlFor="negativePrompt" className="text-gray-200" data-sentry-element="Label" data-sentry-source-file="index.tsx">
                            Negative Prompt:
                        </Label>
                        <Textarea id="negativePrompt" value={negativePrompt} onChange={e => setNegativePrompt(e.target.value)} placeholder="Describe what you don't want in the audio..." className="resize-none focus:!ring-gray-600 focus:!border-gray-600 transition-all duration-300 placeholder-gray-500 h-32 bg-slate-900 text-gray-200 border-gray-700" data-sentry-element="Textarea" data-sentry-source-file="index.tsx" />
                    </div>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label htmlFor="duration" className="text-gray-200" data-sentry-element="Label" data-sentry-source-file="index.tsx">
                                Duration (seconds)
                            </Label>
                            <span className="text-sm text-gray-400">
                                {formValues["Duration"]}s
                            </span>
                        </div>
                        <Slider id="duration" min={1} max={maxDuration} step={1} value={[parseInt(formValues["Duration"])]} onValueChange={value => {
            setFormValues(prev => {
              return {
                ...prev,
                Duration: value[0].toString()
              };
            });
          }} className="[&_[role=slider]]:bg-emerald-600" data-sentry-element="Slider" data-sentry-source-file="index.tsx" />
                    </div>
                    <div className="rounded-lg bg-gradient-to-r from-amber-500/10 to-yellow-500/10 p-4">
                        <div className="text-base text-amber-400 flex items-center justify-center space-x-2">
                            <Sparkles className="h-5 w-5" data-sentry-element="Sparkles" data-sentry-source-file="index.tsx" />
                            <span>
                                Gold Cost: {formValues["Duration"]} Gold
                            </span>
                        </div>
                    </div>
                    <div className="flex justify-end space-x-4">
                        <Button variant="outline" onClick={handleCancel} className="text-gray-200 border-gray-700 hover:bg-gray-700 hover:text-white transition-colors duration-300 ease-in-out px-4 py-2" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                            Cancel
                        </Button>
                        <Button onClick={handleSubmit} disabled={loading} className="!bg-emerald-600 hover:!bg-emerald-700 text-white transition-all duration-300 ease-in-out transform hover:scale-105 px-4 py-2" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                            {loading ? <>
                                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                    Processing...
                                </> : <>
                                    <Music className="mr-2 h-5 w-5" />
                                    Add Audio
                                </>}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>;
}